import React, { FC } from 'react';
import { action, runInAction } from 'mobx';
import { DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import Campaign from 'models/Campaign';
import Widget from 'models/Widget';
import { qaLabelCampaigns } from '../../../../../../../../common-qa-labels/campaigns';
import { buildDynamicLabel } from '../../../../../../../../common-qa-labels/utils';

const WIDGET_DRAG_TYPE = Symbol('widget-row');

interface Item {
    index?: number;
}

interface CollectProps {
    isDragging: boolean;
}

interface Props {
    campaign: Campaign;
    widget: Widget;
}
const WidgetRow: FC<Props> = ({ campaign, widget }) => {
    const row = React.useRef<HTMLTableRowElement>(null);

    const [{ isDragging }, ref, preview] = useDrag<Item, never, CollectProps>(() => ({
        type: WIDGET_DRAG_TYPE,
        item: {
            index: campaign.widgets?.indexOf(widget)
        },
        collect(monitor) {
            return {
                isDragging: monitor.isDragging()
            };
        }
    }));

    const [, drop] = useDrop<Item, never, never>(() => ({
        accept: WIDGET_DRAG_TYPE,
        hover(item, monitor: DropTargetMonitor<Item, never>) {
            const dragIndex = item.index;
            const hoverIndex = campaign.widgets?.indexOf(widget);

            if (dragIndex === hoverIndex) {
                return;
            }

            if (!row.current) {
                return;
            }
            const hoverBoundingRect = row.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex! && hoverClientY < hoverMiddleY) {
                return;
            }

            if (dragIndex > hoverIndex! && hoverClientY > hoverMiddleY) {
                return;
            }

            runInAction(() => {
                const w = campaign.widgets?.[item.index];

                campaign.widgets?.splice(dragIndex, 1);
                if (w) campaign.widgets?.splice(hoverIndex!, 0, w);

                monitor.getItem().index = hoverIndex;
            });
        }
    }));

    drop(preview(row));

    return (
        <tr data-testid={buildDynamicLabel(qaLabelCampaigns.editModal.widgetRow, widget.type?.toString())} ref={row} style={{ visibility: isDragging ? 'hidden' : 'visible' }}>
            <td ref={ref} style={{ cursor: 'grab' }}>
                <FontAwesome icon="grip-vertical" />
            </td>
            <td>{widget.type}</td>
            <td>{widget.content}</td>
            <td>
                <a data-testid={qaLabelCampaigns.editModal.deleteWidgetRow} onClick={action(() => campaign.widgets?.remove(widget))}>Delete</a>
            </td>
        </tr>
    );
};

export default WidgetRow;
